import I from "./Icons";
import Dropdown from "./Dropdown";
import { useTables } from "../report-builder";

interface FormulaStep {
    type: "field" | "op";
    field?: string;
    table?: string;
    dateField?: string;
    agg?: string;
    operator?: string;
    value?: number;
    operand?: {
        type: "field" | "value";
        field?: string;
        table?: string;
        dateField?: string;
        agg?: string;
        value?: number;
    };
}

interface FormulaBuilderProps {
    graph: any;
    onUpdate: (graph: any) => void;
    tbl: any;
    theme: any;
}

function FormulaBuilder({ graph, onUpdate, tbl, theme }: FormulaBuilderProps) {
    const TABLES = useTables();
    const currentTable = graph.dataTable;
    
    // Helper to get numeric fields for a specific table
    const getNumericFields = (tableName?: string) => {
        const table = TABLES.find(t => t.id === (tableName || currentTable));
        return table?.numeric_fields || [];
    };
    
    // Helper to get date fields for a specific table
    const getDateFields = (tableName?: string) => {
        const table = TABLES.find(t => t.id === (tableName || currentTable));
        return table?.date_fields || [];
    };
    
    const numericFields: string[] = tbl?.numeric_fields || [];
    const fieldOptions = numericFields.map((f: string) => ({ v: f, l: f.replace(/_/g, " ") }));
    const tableOptions = TABLES.map(t => ({ v: t.id, l: t.name }));
    const aggOptions = [
        { v: "sum", l: "Sum" },
        { v: "avg", l: "Avg" },
        { v: "count", l: "Count" },
        { v: "min", l: "Min" },
        { v: "max", l: "Max" },
    ];
    const opOptions = [
        { v: "+", l: "+" },
        { v: "-", l: "−" },
        { v: "*", l: "×" },
        { v: "/", l: "÷" },
    ];

    const defaultField = numericFields[0] || "";
    const defaultDateField = getDateFields(currentTable)[0] || "";

    const steps: FormulaStep[] = graph.formula?.steps || [
        { type: "field", field: defaultField, table: currentTable, dateField: defaultDateField, agg: "sum" },
    ];
    
    // Ensure all steps have table and dateField from parent graph if missing
    const normalizedSteps = steps.map(step => {
        if (step.type === "field") {
            return {
                ...step,
                table: step.table || currentTable,
                dateField: step.dateField || defaultDateField,
                agg: step.agg || "sum",
            };
        } else if (step.type === "op" && step.operand?.type === "field") {
            return {
                ...step,
                operand: {
                    ...step.operand,
                    table: step.operand.table || currentTable,
                    dateField: step.operand.dateField || defaultDateField,
                    agg: step.operand.agg || "sum",
                },
            };
        }
        return step;
    });

    const isValidField = (field: string, tableName?: string) => {
        const fields = getNumericFields(tableName);
        return fields.includes(field);
    };

    const safeField = (field: string, tableName?: string) => {
        const fields = getNumericFields(tableName);
        return fields.includes(field) ? field : (fields[0] || "");
    };

    const setSteps = (next: FormulaStep[]) =>
        onUpdate({ ...graph, formula: { steps: next } });

    const updateStep = (index: number, patch: Partial<FormulaStep>) =>
        setSteps(normalizedSteps.map((s, i) => (i === index ? { ...s, ...patch } : s)));

    const updateOperand = (index: number, patch: any) =>
        setSteps(
            normalizedSteps.map((s, i) =>
                i === index ? { ...s, operand: { ...s.operand, ...patch } } : s
            )
        );

    const addStep = () =>
        setSteps([
            ...normalizedSteps,
            {
                type: "op",
                operator: "+",
                operand: { type: "field", field: safeField("", currentTable), table: currentTable, dateField: getDateFields(currentTable)[0] || "", agg: "sum" },
            },
        ]);

    const removeStep = (index: number) => {
        if (normalizedSteps.length <= 1) return;
        setSteps(normalizedSteps.filter((_, i) => i !== index));
    };

    const OperandPicker = ({
                               operand,
                               onChange,
                           }: {
        operand: any;
        onChange: (val: any) => void;
    }) => {
        const isValue = operand?.type === "value";
        const operandTable = operand?.table || currentTable;
        const operandFields = getNumericFields(operandTable);
        const operandDateFields = getDateFields(operandTable);
        const operandFieldOptions = operandFields.map((f: string) => ({ v: f, l: f.replace(/_/g, " ") }));
        const operandDateFieldOptions = operandDateFields.map((f: string) => ({ v: f, l: f.replace(/_/g, " ") }));
        const fieldInvalid = !isValue && operand?.field && !isValidField(operand.field, operandTable);

        return (
            <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                    <button
                        onClick={() =>
                            onChange(
                                isValue
                                    ? { type: "field", field: safeField("", currentTable), table: currentTable, agg: "sum", dateField: getDateFields(currentTable)[0] || "" }
                                    : { type: "value", value: 1 }
                            )
                        }
                        title={isValue ? "Literal number" : "Table field"}
                        style={{
                            padding: "4px 8px",
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 600,
                            background: theme.cardBackground,
                            border: `1px solid ${theme.border}`,
                            color: theme.textSecondary,
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {isValue ? "#" : "ƒ"}
                    </button>
                    {isValue ? (
                        <input
                            type="number"
                            value={operand?.value ?? ""}
                            onChange={(e) =>
                                onChange({ type: "value", value: parseFloat(e.target.value) || 0 })
                            }
                            style={{
                                flex: 1,
                                padding: "6px 10px",
                                background: theme.cardBackground,
                                border: `1px solid ${theme.border}`,
                                borderRadius: 6,
                                color: theme.text,
                                fontSize: 13,
                                outline: "none",
                            }}
                        />
                    ) : (
                        <div
                            style={{
                                display: "flex",
                                gap: 4,
                                flex: 1,
                                ...(fieldInvalid
                                    ? { outline: `2px solid ${theme.error}`, borderRadius: 6 }
                                    : {}),
                            }}
                        >
                            <div style={{ flex: 1.5 }}>
                                <Dropdown
                                    label=""
                                    value={operandTable}
                                    options={tableOptions}
                                    onChange={(v: string) => {
                                        const newFields = getNumericFields(v);
                                        const newDateFields = getDateFields(v);
                                        const newField = newFields[0] || "";
                                        const newDateField = newDateFields[0] || "";
                                        onChange({ ...operand, type: "field", table: v, field: newField, dateField: newDateField });
                                    }}
                                    theme={theme}
                                />
                            </div>
                            <div style={{ flex: 2 }}>
                                <Dropdown
                                    label=""
                                    value={isValidField(operand?.field, operandTable) ? operand.field : ""}
                                    options={operandFieldOptions}
                                    onChange={(v: string) =>
                                        onChange({ ...operand, type: "field", field: v })
                                    }
                                    theme={theme}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <Dropdown
                                    label=""
                                    value={operand?.agg || "sum"}
                                    options={aggOptions}
                                    onChange={(v: string) => onChange({ ...operand, agg: v })}
                                    theme={theme}
                                />
                            </div>
                        </div>
                    )}
                </div>
                {!isValue && operandDateFieldOptions.length > 0 && (
                    <div style={{ display: "flex", gap: 4, paddingLeft: 36 }}>
                        <div style={{ flex: 1 }}>
                            <Dropdown
                                label=""
                                value={operand?.dateField || operandDateFieldOptions[0]?.v || ""}
                                options={operandDateFieldOptions}
                                onChange={(v: string) => onChange({ ...operand, dateField: v })}
                                theme={theme}
                            />
                        </div>
                        <div style={{ 
                            fontSize: 11, 
                            color: theme.textSecondary, 
                            padding: "6px 8px",
                            display: "flex",
                            alignItems: "center",
                        }}>
                            (Date Field for JOIN)
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Build preview string
    const previewParts = normalizedSteps.map((s, i) => {
        if (i === 0) {
            if ("value" in s) return String((s as any).value ?? 0);
            const stepTable = s.table || currentTable;
            const label = isValidField(s.field || "", stepTable)
                ? (s.field || "").replace(/_/g, " ")
                : "???";
            const tablePrefix = stepTable !== currentTable ? `${stepTable}.` : "";
            return `${(s.agg || "sum").toUpperCase()}(${tablePrefix}${label})`;
        }
        const op = s.operator || "+";
        const o = s.operand;
        if (!o) return ` ${op} ???`;
        if (o.type === "value") return ` ${op} ${o.value ?? 0}`;
        const operandTable = o.table || currentTable;
        const label = isValidField(o.field || "", operandTable)
            ? (o.field || "").replace(/_/g, " ")
            : "???";
        const tablePrefix = operandTable !== currentTable ? `${operandTable}.` : "";
        return ` ${op} ${(o.agg || "sum").toUpperCase()}(${tablePrefix}${label})`;
    });

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div
                style={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: ".08em",
                    color: theme.textSecondary,
                }}
            >
                Formula
            </div>

            {normalizedSteps.map((step, i) => (
                <div key={i} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    {i === 0 ? (
                        <OperandPicker
                            operand={step}
                            onChange={(val: any) => updateStep(i, val)}
                        />
                    ) : (
                        <>
                            <div style={{ width: 48 }}>
                                <Dropdown
                                    label=""
                                    value={step.operator || "+"}
                                    options={opOptions}
                                    onChange={(v: string) => updateStep(i, { operator: v })}
                                    theme={theme}
                                />
                            </div>
                            <OperandPicker
                                operand={step.operand}
                                onChange={(val: any) => updateOperand(i, val)}
                            />
                            <button
                                onClick={() => removeStep(i)}
                                style={{
                                    background: "none",
                                    border: "none",
                                    color: theme.textSecondary,
                                    cursor: "pointer",
                                    padding: 2,
                                    display: "flex",
                                }}
                                onMouseEnter={(e) =>
                                    (e.currentTarget.style.color = theme.error)
                                }
                                onMouseLeave={(e) =>
                                    (e.currentTarget.style.color = theme.textSecondary)
                                }
                            >
                                <I.Trash />
                            </button>
                        </>
                    )}
                </div>
            ))}

            <button
                onClick={addStep}
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 4,
                    padding: "6px 0",
                    background: theme.cardBackground,
                    border: `1px dashed ${theme.border}`,
                    borderRadius: 6,
                    color: theme.textSecondary,
                    fontSize: 12,
                    cursor: "pointer",
                }}
            >
                <I.Plus /> Add Step
            </button>

            <div
                style={{
                    padding: "4px 8px",
                    background: `${theme.blue}10`,
                    borderRadius: 4,
                    fontSize: 11,
                    color: theme.textSecondary,
                    fontFamily: "monospace",
                }}
            >
                {previewParts.join("")}
            </div>
        </div>
    );
}

export default FormulaBuilder;